/* eslint-disable react-refresh/only-export-components */
"use client";

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

interface LoadingContextType {
	isLoading: boolean;
	setLoading: (loading: boolean, message?: string) => void;
	loadingMessage?: string;
}

const LoadingContext = createContext<LoadingContextType | null>(null);

export function useGlobalLoading() {
	const context = useContext(LoadingContext);
	if (!context) {
		throw new Error(
			"useGlobalLoading must be used within a GlobalLoadingProvider",
		);
	}
	return context;
}

/**
 * Drive the global loading modal while `active` is true, clearing it on cleanup.
 * Single shared implementation of the `setLoading(active); return () =>
 * setLoading(false)` effect that several screens used to repeat verbatim — so
 * the show/cleanup pair can't drift between call sites.
 */
export function useGlobalLoadingWhile(active: boolean, message?: string) {
	const { setLoading } = useGlobalLoading();
	useEffect(() => {
		setLoading(active, message);
		return () => setLoading(false);
	}, [active, message, setLoading]);
}

export function GlobalLoadingProvider({ children }: { children: ReactNode }) {
	const [isLoading, setIsLoading] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState<string>();

	const setLoading = useCallback((loading: boolean, message?: string) => {
		setIsLoading(loading);
		setLoadingMessage(message);
	}, []); // Empty deps - setIsLoading and setLoadingMessage are stable from useState

	return (
		<LoadingContext.Provider value={{ isLoading, setLoading, loadingMessage }}>
			{children}
		</LoadingContext.Provider>
	);
}
